import type { FC } from 'react'
import { Paper, Checkbox, Typography, Box, Chip, Tooltip, InputAdornment } from '@mui/material'
import { HolderOutlined } from '@ant-design/icons'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { TextField } from 'components/@extended/TextField'

interface PackageRewardCardProps {
  rewardId: string
  rewardName: string
  supportsEmployeeContribution: boolean
  isSelected: boolean
  companyContribution: number
  employeeContribution: number
  onToggleSelect: (id: string) => void
  onContributionChange: (id: string, field: 'companyContribution' | 'employeeContribution', value: string) => void
  formatCurrency: (value: number) => string
  disableDragAndDrop?: boolean
  hideSelection?: boolean
}

export const PackageRewardCard: FC<PackageRewardCardProps> = ({
  rewardId,
  rewardName,
  supportsEmployeeContribution,
  isSelected,
  companyContribution,
  employeeContribution,
  onToggleSelect,
  onContributionChange,
  formatCurrency,
  disableDragAndDrop = false,
  hideSelection = false,
}) => {
  const sortableProps = useSortable({ id: rewardId })
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortableProps

  const style = disableDragAndDrop
    ? {}
    : {
        transform: CSS.Transform.toString(transform),
        transition,
      }

  return (
    <Paper
      ref={disableDragAndDrop ? undefined : setNodeRef}
      style={style}
      elevation={0}
      variant="outlined"
      sx={{
        px: 1.5,
        py: 0.5,
        mb: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        opacity: !disableDragAndDrop && isDragging ? 0.5 : isSelected ? 1 : 0.6,
        '&:hover': {
          backgroundColor: 'action.hover',
          boxShadow: 1,
        },
      }}
    >
      {!disableDragAndDrop && (
        <Box
          {...attributes}
          {...listeners}
          sx={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            color: 'text.secondary',
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        >
          <HolderOutlined style={{ fontSize: '16px' }} />
        </Box>
      )}

      {!hideSelection && (
        <Checkbox
          checked={isSelected}
          onChange={() => onToggleSelect(rewardId)}
          onClick={(e) => e.stopPropagation()}
          size="small"
        />
      )}

      <Box sx={{ flexGrow: 1, minWidth: 120 }}>
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          {rewardName}
        </Typography>
      </Box>

      {supportsEmployeeContribution && (
        <Tooltip title="Employee Contribution Allowed">
          <Chip label="EC" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }} />
        </Tooltip>
      )}

      <TextField
        variant="outlined"
        value={formatCurrency(companyContribution)}
        onChange={(e) => onContributionChange(rewardId, 'companyContribution', e.target.value)}
        disabled={!isSelected}
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        sx={{ width: 140 }}
        size="small"
      />

      <TextField
        variant="outlined"
        value={formatCurrency(employeeContribution)}
        onChange={(e) => onContributionChange(rewardId, 'employeeContribution', e.target.value)}
        disabled={!isSelected || !supportsEmployeeContribution}
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        sx={{ width: 140 }}
        size="small"
      />
    </Paper>
  )
}
